import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { useFormik } from 'formik';
import Button from 'components/shared/button';
import InputField from 'components/shared/input-field';
import SelectInput from 'components/shared/select-input';
import { formatWithCommas } from 'functions/currencyConveter';

import { saveFormData } from 'store/slices/houseRent';
import {
  houseTypes,
  Step2Props,
  Step2Types,
  step2ValidationSchema,
} from './utils';
import { useAppSelector } from 'store/hooks';
import { useBanks } from 'hooks/useBanks';

export const Step2: React.FC<Step2Props> = ({
  prevStep,
  nextStep,
  handleInputDataUpdate,
}) => {
  const dispatch = useDispatch();
  const {
    bankOptions: banks,
    loading: bankLoading,
    error,
    refetch,
  } = useBanks();

  const saved = useAppSelector((s) => s.houseRent.formData.step2);
  const applicationId = useAppSelector(
    (state) => state.houseRent.applicationId
  );

  const [loading, setLoading] = useState(false);
  const [changeType, setChangeType] = useState(false);

  // if previously saved type was "Others", show text input by default
  useEffect(() => {
    if (saved?.houseType === 'Others') setChangeType(true);
  }, [saved?.houseType]);

  const initialValues: Step2Types = {
    RentPrice: saved?.RentPrice ?? '',
    RequestedAmount: saved?.RequestedAmount ?? '',
    houseType: saved?.houseType ?? '',
    LandlordName: saved?.LandlordName ?? '',
    LandlordEmail: saved?.LandlordEmail ?? '',
    LandlordAccountName: saved?.LandlordAccountName ?? '',
    LandlordAccountNumber: saved?.LandlordAccountNumber ?? '',
    LandlordBankName: saved?.LandlordBankName ?? '',
    LandlordContactNumber: saved?.LandlordContactNumber ?? '',
  };

  const formik = useFormik<Step2Types>({
    initialValues,
    validationSchema: step2ValidationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const id = applicationId || uuid();

        // Save raw values (without commas) to store
        const payload: Step2Types = {
          ...values,
          RentPrice: values.RentPrice,
          RequestedAmount: values.RequestedAmount,
        };

        dispatch(
          saveFormData({
            applicationId: id,
            formData: payload,
            step: 'step2',
          })
        );
        handleInputDataUpdate(payload);
        nextStep();
      } catch (err) {
        console.error('Save & Continue (step2) failed:', err);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleSelectApartment = (name: string, value: any) => {
    if (value?.value === 'others') {
      setChangeType(true);
      formik.setFieldValue(name, '');
    } else {
      setChangeType(false);
      formik.setFieldValue(name, value?.label || '');
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const plainValue = value.replace(/,/g, '');
    if (name === `RentPrice`) {
      const formattedValue = formatWithCommas(plainValue);
      formik.setFieldValue(`RentPrice`, formattedValue);
      formik.handleChange(e);
    } else {
      formik.handleChange(e);
    }
    // formik.handleChange(e);
  };
  return (
    <form>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {/* Rent Amount */}
        <div className="col-span-1">
          <InputField
            type="text"
            label="Rent Amount"
            name="RentPrice"
            placeholder="Rent amount"
            value={formatWithCommas(formik.values.RentPrice?.toString() || '')}
            onChange={handleInputChange}
            onBlur={formik.handleBlur}
            required
            error={
              formik.touched.RentPrice ? formik.errors.RentPrice : undefined
            }
          />
        </div>

        {/* Requested Amount */}
        <div className="col-span-1">
          <InputField
            type="text"
            label="Requested Amount"
            name="RequestedAmount"
            placeholder="Requested amount"
            value={formatWithCommas(
              formik?.values?.RequestedAmount.toString() || ''
            )}
            onChange={handleInputChange}
            onBlur={formik.handleBlur}
            required
            error={
              formik.touched.RequestedAmount
                ? formik.errors.RequestedAmount
                : undefined
            }
          />
        </div>

        {/* Apartment Type */}
        <div className="col-span-1 state-input">
          {changeType ? (
            <InputField
              type="text"
              label="Type of Apartment"
              name="houseType"
              placeholder="Enter your Apartment Type"
              value={formik.values.houseType}
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              required
              error={
                formik.touched.houseType ? formik.errors.houseType : undefined
              }
            />
          ) : (
            <SelectInput
              label="Type of Apartment"
              options={houseTypes}
              placeholder="Select your Apartment Type"
              name="houseType"
              required
              value={
                houseTypes.find(
                  (option) => option?.label === formik.values.houseType
                ) || null
              }
              onChange={handleSelectApartment}
              // onBlur={formik.handleBlur}
              error={
                formik.touched.houseType ? formik.errors.houseType : undefined
              }
            />
          )}
        </div>

        {/* Landlord Information */}
        <div className="col-span-1">
          <InputField
            label="Landlord Name"
            type="text"
            name="LandlordName"
            placeholder="Landlord Name"
            value={formik.values.LandlordName}
            onChange={handleInputChange}
            onBlur={formik.handleBlur}
            required
            error={
              formik.touched.LandlordName
                ? formik.errors.LandlordName
                : undefined
            }
          />
        </div>

        <div className="col-span-1">
          <InputField
            label="Landlord Email (optional)"
            type="email"
            name="LandlordEmail"
            placeholder="Landlord Email Address"
            value={formik.values.LandlordEmail}
            onChange={handleInputChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.LandlordEmail
                ? formik.errors.LandlordEmail
                : undefined
            }
          />
        </div>

        <div className="col-span-1">
          <InputField
            label="Landlord Account Name"
            type="text"
            name="LandlordAccountName"
            placeholder="Landlord Account Name"
            value={formik.values.LandlordAccountName}
            onChange={handleInputChange}
            onBlur={formik.handleBlur}
            required
            error={
              formik.touched.LandlordAccountName
                ? formik.errors.LandlordAccountName
                : undefined
            }
          />
        </div>

        <div className="col-span-1">
          <InputField
            label="Landlord Account Number"
            type="text"
            name="LandlordAccountNumber"
            placeholder="Landlord Account Number"
            value={formik.values.LandlordAccountNumber}
            onChange={handleInputChange}
            onBlur={formik.handleBlur}
            required
            error={
              formik.touched.LandlordAccountNumber
                ? formik.errors.LandlordAccountNumber
                : undefined
            }
          />
        </div>

        <div className="col-span-1">
          <SelectInput
            label="Landlord Bank Name"
            name="LandlordBankName"
            options={banks}
            placeholder={
              bankLoading ? 'Loading banks...' : 'Landlord Bank Name'
            }
            required
            value={
              banks.find(
                (option) => option.label === formik.values.LandlordBankName
              ) || null
            }
            onChange={(name, value) =>
              formik.setFieldValue(name, value?.label || '')
            }
            error={
              formik.touched.LandlordBankName
                ? formik.errors.LandlordBankName
                : undefined
            }
          />

          {error && (
            <div className="text-red-500 text-sm mt-1">
              Failed to load banks.{' '}
              <button
                type="button"
                onClick={refetch}
                className="underline text-blue-600"
              >
                Retry
              </button>
            </div>
          )}
        </div>

        <div className="col-span-1">
          <InputField
            label="Landlord Contact Number"
            type="text"
            name="LandlordContactNumber"
            placeholder="Landlord Contact Number"
            value={formik.values.LandlordContactNumber}
            onChange={handleInputChange}
            onBlur={formik.handleBlur}
            required
            error={
              formik.touched.LandlordContactNumber
                ? formik.errors.LandlordContactNumber
                : undefined
            }
          />
        </div>
      </div>

      <div className="w-full justify-between flex items-center mt-6">
        <Button
          type="button"
          onClick={prevStep}
          className="bg-white border text-BrandPrimary border-BrandPrimary"
        >
          Back
        </Button>

        <Button
          type="button"
          onClick={() => formik.handleSubmit()}
          disabled={loading || !formik.isValid}
        >
          {loading ? 'Saving...' : 'Save and Continue'}
        </Button>
      </div>
    </form>
  );
};
